import Loading from 'Shared/components/Loading'
import restClient from 'Shared/hooks/restClient'
import LookupDecisionMakers from './LookupDecisionMakers'
import EmailDecisionMakers from './EmailDecisionMakers'
import useLanguage from 'FlowBuilder/hooks/useLanguageFlow'
import { EVENTS, firePageEvent } from 'FlowBuilder/lib/pageEvents'

export default function EmailToTarget({ action, onSubmit, onSkip, onUpdateNoTarget = () => {} }) {
  const isCustomDecisionMakers = !!action.config?.customDecisionMakers
  const initialDecisionMakers = isCustomDecisionMakers ? (action.config?.decisionMakers || []).filter(dm => dm.active !== false) : []

  const [step, setStep] = useState(isCustomDecisionMakers ? 1 : 0)
  const decisionMakerSetId = action.config?.decisionMakerSetId
  const [decisionMakerSet, setDecisionMakerSet] = useState({})
  const [decisionMakers, setDecisionMakers] = useState(initialDecisionMakers)
  const [loading, setLoading] = useState(!isCustomDecisionMakers)
  const [hasReset, setHasReset] = useState(false)
  const [postcode, setPostcode] = useState('')

  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [initialBody, setInitialBody] = useState('')

  const flowId = useSel(s => s.taker?.flow?.id || s.builder.flow.id)
  const actionId = action.id
  const stepRef = useRef(null);

  const language = useLanguage();

  useEffect(() => {
    if (!loading && (step === 0 || step === 1)) {
      firePageEvent(EVENTS.EMAIL_TO_TARGET_STEP_CHANGE, { step }, stepRef.current || document)
    }
  }, [step, loading])

  useEffect(() => {
    if (!decisionMakerSetId || isCustomDecisionMakers) return;

    restClient.get(`/api/target/decision_maker_sets/${decisionMakerSetId}`)
      .then(({ data }) => {
        setDecisionMakerSet(data)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [decisionMakerSetId])

  function renderActionTemplate() {
    return restClient.get(`/api/target/flows/${flowId}/render_action_template`, { params: { actionId, decisionMakerIds: decisionMakers.map(dm => dm.id), language } })
  }

  useEffect(() => {
    if (step === 0 && decisionMakers.length > 0) {
      setStep(1)
    }
  }, [JSON.stringify(decisionMakers)])

  function reset() {
    setStep(0)
    setDecisionMakers([])
    setHasReset(true)
  }

  const shouldAutoLookup = useMemo(() => decisionMakerSet.lookupKind === 'manual' && !action.config?.showIntroduction, [decisionMakerSet, action])

  if (loading) return <Loading />
  if (decisionMakerSet.decisionMakerCount === 0) return <div>
    No decision makers are set up for this action.
  </div>

  return <>
    <div ref={stepRef}></div>
    {step === 0 && !action.config?.customDecisionMakers &&
      <LookupDecisionMakers
        decisionMakerSet={decisionMakerSet}
        setDecisionMakers={setDecisionMakers}
        hasReset={hasReset}
        setLoading={setLoading}
        action={action}
        onSkip={onSkip}
        setPostcode={setPostcode}
        autoLookup={shouldAutoLookup}
      />
    }
    {step === 1 && (decisionMakers.length > 0 || action.config?.customDecisionMakers) &&
      <EmailDecisionMakers
        decisionMakers={decisionMakers}
        onSubmit={onSubmit}
        renderActionTemplate={renderActionTemplate}
        action={action}
        reset={reset}
        canReset={!isCustomDecisionMakers && !shouldAutoLookup}
        onUpdateNoTarget={onUpdateNoTarget}
        postcode={postcode}
        setBody={setBody}
        setSubject={setSubject}
        subject={subject}
        body={body}
        initialBody={initialBody}
        setInitialBody={setInitialBody}
      />
    }
    {step === 1 && decisionMakers.length === 0 && !action.config?.customDecisionMakers && "No one to email"}
  </>
}
